import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Chip, Alert,
  CircularProgress, Select, MenuItem, Menu, IconButton, Tooltip,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { adminClient } from '../data/adminClient';
import { suggestSupervisors, checkSupervisors, supervisorTargets } from '../../shared/workshopScheduler';
import { useEventReadOnly } from '../adminAccess';

const sessionLabel = (s) => `${s.name}${s.time ? ` · ${s.time}` : ''}`;
const personName = (s) => `${s.firstName} ${s.lastName}`.trim();
const runLabel = (r) => `${r.title} — ${r.spaceName}`;

// Admin › Workshops › Supervisors. Teachers tick who can supervise on their
// portal's Teachers / Chaperones page; here each supervisor gets one
// workshop in Session 1 and one in Session 2. Every workshop needs at least
// one; Suggest gives extras to the bigger workshops. Saved together.
export default function SupervisorsSection() {
  const readOnly = useEventReadOnly(); // view-only admin: assignments shown, not editable
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({}); // staffId -> { session1, session2 }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [suggestAnchor, setSuggestAnchor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setData(await adminClient.getWorkshopSupervisors());
      setEdits({});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Supervisors with unsaved changes applied.
  const current = useMemo(() => (data
    ? data.supervisors.map(s => ({ ...s, ...(edits[s.staffId] || {}) }))
    : []), [data, edits]);
  const issues = useMemo(() => (data
    ? checkSupervisors({ runs: data.runs, supervisors: current, sessionNames: { 1: data.sessions[1].name, 2: data.sessions[2].name } })
    : []), [data, current]);

  const setChoice = (s, key, runId) => {
    const original = data.supervisors.find(x => x.staffId === s.staffId);
    const next = { session1: s.session1, session2: s.session2, [key]: runId };
    setEdits(prev => {
      const copy = { ...prev };
      if (next.session1 === original.session1 && next.session2 === original.session2) delete copy[s.staffId];
      else copy[s.staffId] = next;
      return copy;
    });
  };

  const runSuggest = (mode) => {
    setSuggestAnchor(null);
    const suggested = suggestSupervisors({ runs: data.runs, supervisors: current, mode });
    const next = {};
    suggested.forEach(a => {
      const original = data.supervisors.find(x => x.staffId === a.staffId);
      if (a.session1 !== original.session1 || a.session2 !== original.session2) next[a.staffId] = { session1: a.session1, session2: a.session2 };
    });
    setEdits(next);
    setNotice({ severity: 'info', text: 'Suggested a supervisor for every workshop, with extras in the bigger ones. Review it, then Save.' });
  };

  const save = async () => {
    setSaving(true); setError(null);
    try {
      // The save sends back the updated page — no second request.
      const { changed, view } = await adminClient.saveWorkshopSupervisors(Object.entries(edits).map(([staffId, e]) => ({ staffId, ...e })));
      setNotice({ severity: 'success', text: `Saved ${changed} change${changed === 1 ? '' : 's'}.` });
      setData(view);
      setEdits({});
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (!data) return error ? <Alert severity="error">{error}</Alert> : null;

  const changeCount = Object.keys(edits).length;
  const errors = issues.filter(i => i.severity === 'error');
  const stale = data.supervisors.filter(s => s.stale).length;
  const assignedLabel = (runId) => { const run = data.runs.find(r => r.runId === runId); return run ? runLabel(run) : 'Not assigned'; };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip size="small" variant="outlined" label={`${data.supervisors.length} supervisor${data.supervisors.length === 1 ? '' : 's'}`} />
        <Chip size="small" variant="outlined" color={errors.length ? 'error' : 'success'}
          label={errors.length ? 'Some workshops have no supervisor' : 'Every workshop has a supervisor'} />
        {changeCount > 0 && <Chip size="small" color="info" label="Unsaved changes" />}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Reload (discards unsaved changes)"><span><IconButton size="small" onClick={load} disabled={loading || saving}><RefreshIcon fontSize="small" /></IconButton></span></Tooltip>
        {!readOnly && <Button size="small" variant="outlined" startIcon={<AutoFixHighIcon />} onClick={e => setSuggestAnchor(e.currentTarget)}
          disabled={!data.runs.length || !data.supervisors.length}>Suggest</Button>}
        <Menu anchorEl={suggestAnchor} open={!!suggestAnchor} onClose={() => setSuggestAnchor(null)}>
          <MenuItem onClick={() => runSuggest('fill')}>Fill the gaps (keep what’s assigned)</MenuItem>
          <MenuItem onClick={() => runSuggest('fresh')}>Start over</MenuItem>
        </Menu>
        {changeCount > 0 && <Button size="small" onClick={() => setEdits({})} disabled={saving}>Discard</Button>}
        {!readOnly && <Button size="small" variant="contained" onClick={save} disabled={!changeCount || saving}>{saving ? <CircularProgress size={18} color="inherit" /> : 'Save'}</Button>}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice.text}</Alert>}
      {data.runs.length === 0 && <Alert severity="info" sx={{ mb: 2 }}>No workshops are on the schedule yet — build it on the Schedule page first.</Alert>}
      {data.schoolsWithout.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {data.schoolsWithout.length === 1 ? 'This school hasn’t' : 'These schools haven’t'} ticked a workshop supervisor on their Teachers / Chaperones page yet: {data.schoolsWithout.join(', ')}.
        </Alert>
      )}
      {stale > 0 && <Alert severity="warning" sx={{ mb: 2 }}>{stale} supervisor{stale === 1 ? ' was' : 's were'} assigned to a workshop since removed from the schedule — shown as unassigned.</Alert>}
      {issues.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          {issues.map((i, k) => (
            <Box key={k} sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 0.25 }}>
              {i.severity === 'error' ? <ErrorOutlineIcon fontSize="small" color="error" /> : <WarningAmberIcon fontSize="small" color="warning" />}
              <Typography variant="body2">{i.message}</Typography>
            </Box>
          ))}
        </Paper>
      )}

      {/* Each session's workshops and who's supervising */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
        {[1, 2].map(session => {
          const runs = data.runs.filter(r => r.session === session);
          const targets = supervisorTargets(runs, current.length);
          return (
            <Paper key={session} variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>{sessionLabel(data.sessions[session])}</Typography>
              {runs.length === 0 && <Typography variant="caption" color="text.secondary">No workshops in this session.</Typography>}
              {runs.map(r => {
                const mine = current.filter(s => s[`session${session}`] === r.runId);
                return (
                  <Box key={r.runId} sx={{ py: 0.75, borderTop: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                      <Typography variant="body2">{r.title} <Typography component="span" variant="caption" color="text.secondary">· {r.spaceName}</Typography></Typography>
                      <Typography variant="caption" color={mine.length ? 'text.secondary' : 'error'} sx={{ whiteSpace: 'nowrap' }}>
                        {mine.length} / {targets.get(r.runId) || 1} suggested · {r.assigned || r.capacity} dancers
                      </Typography>
                    </Box>
                    <Typography variant="caption" color={mine.length ? 'text.primary' : 'error'}>
                      {mine.length ? mine.map(s => `${personName(s)} (${s.schoolName})`).join(', ') : 'No supervisor'}
                    </Typography>
                  </Box>
                );
              })}
            </Paper>
          );
        })}
      </Box>

      {/* Per supervisor */}
      {data.supervisors.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No supervisors yet — teachers tick “Workshop supervisor” for their teachers/chaperones on the portal’s Teachers / Chaperones page.</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Supervisor</TableCell>
                <TableCell>School</TableCell>
                <TableCell>{data.sessions[1].name}</TableCell>
                <TableCell>{data.sessions[2].name}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {current.map(s => (
                <TableRow key={s.staffId} sx={edits[s.staffId] ? { bgcolor: 'action.selected' } : undefined}>
                  <TableCell>{personName(s)}{s.role && <Typography component="span" variant="caption" color="text.secondary"> · {s.role}</Typography>}</TableCell>
                  <TableCell>{s.schoolName}</TableCell>
                  {[1, 2].map(session => (
                    <TableCell key={session}>
                      {readOnly ? (
                        <Typography variant="body2" color={s[`session${session}`] ? 'text.primary' : 'text.disabled'}>
                          {assignedLabel(s[`session${session}`])}
                        </Typography>
                      ) : <Select size="small" displayEmpty fullWidth value={s[`session${session}`]} sx={{ minWidth: 180 }}
                        onChange={e => setChoice(s, `session${session}`, e.target.value)}>
                        <MenuItem value=""><em>Not assigned</em></MenuItem>
                        {data.runs.filter(r => r.session === session).map(r => (
                          <MenuItem key={r.runId} value={r.runId}>{runLabel(r)}</MenuItem>
                        ))}
                      </Select>}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}
